/**
 * Which 5 GHz radio a 2.4 GHz one mirrors to — "same name and password on
 * both bands", decided once for every client.
 *
 * The web's WiFi card had this rule inline (`findMirrorTarget` in
 * `DeviceDetail.tsx`); the phone's WiFi card and the ONU-keyed WiFi route
 * (`POST /api/onus/:id/wifi` with `sameOnBoth`) need the same answer, and
 * three copies of "which radio is the pair" would eventually disagree about a
 * unit whose labels are odd. So it lives here, over the smallest shape a
 * radio has to carry, and every caller imports it.
 *
 * The rule: the same SSID number on the other band. On the F670L that is 1
 * with 5 and 2 with 6 — the instance numbers say nothing, so the pairing comes
 * from the labels the ONU type assigns ("2.4G SSID1" pairs with "5G SSID1").
 * With no matching slot, the first 5 GHz radio — right for the common case of
 * one SSID per band, which is every subscriber install.
 *
 * Offered on 2.4 GHz only, and deliberately not the reverse: 2.4 GHz is what
 * gets configured first, so copying back up from 5 GHz would just be a second
 * way to do the same thing in the confusing direction.
 */

/** The least a radio has to say for the pairing to be decided. */
export interface MirrorableRadio {
  band: '2.4G' | '5G' | 'unknown';
  label: string;
  instance: string;
}

/** The SSID slot number a label names ("2.4G SSID1" → "1"), or null. */
function slotOf(label: string): string | null {
  return /ssid\s*(\d+)/i.exec(label)?.[1] ?? null;
}

export function findMirrorTarget<R extends MirrorableRadio>(radio: R, all: readonly R[]): R | null {
  if (radio.band !== '2.4G') return null;
  const candidates = all.filter((other) => other.band === '5G');
  if (candidates.length === 0) return null;

  const slot = slotOf(radio.label);
  if (slot) {
    const paired = candidates.find((other) => slotOf(other.label) === slot);
    if (paired) return paired;
  }
  return candidates[0] ?? null;
}

/**
 * Whether "same on both bands" should start TICKED.
 *
 * Pre-ticked when the two radios already carry the same SSID, since that is
 * how a single-SSID install looks and unticking it would be the surprise —
 * the web card's rule, shared so the phone opens the same way. A 2.4 GHz
 * radio with no name yet (a fresh unit) also starts ticked: one name for the
 * whole unit is what an install-day technician is about to type.
 */
export function mirrorStartsTicked(
  radio: { ssid: string | null },
  target: { ssid: string | null } | null,
): boolean {
  if (!target) return false;
  if (!radio.ssid) return true;
  return radio.ssid === target.ssid;
}
